import { CommonModule } from '@angular/common';
import { Component, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { forkJoin } from 'rxjs';

import { formatCompactOrIsoDate, formatCurrencyAmount } from '../../shared/trade-formatters';
import { InvoiceSummary, InvoiceTypeOption, UpsertVendorInput, Vendor } from '../../invoices/invoices.types';
import { InvoicesService } from '../../invoices/invoices.service';
import { VendorTypeService } from '../vendor-type/vendor-type.service';
import { VendorType } from '../vendor-type/vendor-type.types';

interface VendorFormState {
  name: string;
  vendorTypeId: number | null;
  description: string;
  defaultCurrency: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  stateProvince: string;
  postalCode: string;
  countryCode: string;
  phoneNumber: string;
  emittedInvoiceConceptIds: number[];
}

const EMPTY_VENDOR_FORM: VendorFormState = {
  name: '',
  vendorTypeId: null,
  description: '',
  defaultCurrency: 'USD',
  addressLine1: '',
  addressLine2: '',
  city: '',
  stateProvince: '',
  postalCode: '',
  countryCode: '',
  phoneNumber: '',
  emittedInvoiceConceptIds: [],
};

@Component({
  selector: 'app-vendor-detail',
  imports: [CommonModule, FormsModule, RouterLink],
  templateUrl: './vendor-detail.html',
  styleUrls: ['../vendor-profile.css', './vendor-detail.css'],
})
export class VendorDetailComponent implements OnInit {
  protected readonly vendor = signal<Vendor | null>(null);
  protected readonly invoices = signal<InvoiceSummary[]>([]);
  protected readonly invoiceTypeOptions = signal<InvoiceTypeOption[]>([]);
  protected readonly vendorTypeOptions = signal<VendorType[]>([]);
  protected readonly isEditing = signal(false);
  protected readonly isSaving = signal(false);
  protected readonly attachmentName = signal<string | null>(null);
  protected readonly attachmentFile = signal<File | null>(null);
  protected readonly validationMessage = signal('');
  protected readonly loadError = signal('');
  protected readonly form = signal<VendorFormState>({ ...EMPTY_VENDOR_FORM });

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly invoicesService: InvoicesService,
    private readonly vendorTypeService: VendorTypeService,
  ) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      const vendorId = Number(params.get('vendorId'));
      if (!Number.isFinite(vendorId) || vendorId <= 0) {
        this.vendor.set(null);
        this.invoices.set([]);
        return;
      }

      forkJoin({
        invoiceTypes: this.invoicesService.getInvoiceTypeOptions(),
        vendorTypes: this.vendorTypeService.getActiveVendorTypeOptions(),
        vendor: this.invoicesService.getVendorById(vendorId),
        invoices: this.invoicesService.getInvoicesByVendor(vendorId),
      }).subscribe({
        next: ({ invoiceTypes, vendorTypes, vendor, invoices }) => {
          this.invoiceTypeOptions.set(invoiceTypes);
          this.vendorTypeOptions.set(vendorTypes);
          this.vendor.set(vendor ?? null);
          this.invoices.set(invoices);
          this.isEditing.set(false);
          this.loadError.set(vendor ? '' : 'Vendor not found.');
        },
        error: () => {
          this.vendor.set(null);
          this.invoices.set([]);
          this.loadError.set('Unable to load vendor detail from the API.');
        },
      });
    });
  }

  protected formatDate(value: string): string {
    return formatCompactOrIsoDate(value);
  }

  protected formatAmount(currency: string, amount: number): string {
    return formatCurrencyAmount(currency, amount);
  }

  protected formatAddress(vendor: Vendor): string {
    const cityLine = [vendor.city, vendor.stateProvince, vendor.postalCode]
      .map((part) => (part ?? '').trim())
      .filter((part) => !!part)
      .join(', ');

    const parts = [vendor.addressLine1, vendor.addressLine2, cityLine, vendor.countryCode]
      .map((part) => (part ?? '').trim())
      .filter((part) => !!part);

    return parts.length > 0 ? parts.join(', ') : '';
  }

  protected hasInvoiceType(typeId: number): boolean {
    return this.form().emittedInvoiceConceptIds.includes(typeId);
  }

  protected toggleInvoiceType(typeId: number, selected: boolean): void {
    const current = this.form().emittedInvoiceConceptIds;
    if (selected && !current.includes(typeId)) {
      this.onFieldChange('emittedInvoiceConceptIds', [...current, typeId]);
      return;
    }

    if (!selected && current.includes(typeId)) {
      this.onFieldChange('emittedInvoiceConceptIds', current.filter((item) => item !== typeId));
    }
  }

  protected startEditing(): void {
    const currentVendor = this.vendor();
    if (!currentVendor) {
      return;
    }

    this.validationMessage.set('');
    this.hydrateFormFromVendor(currentVendor);
    this.attachmentFile.set(null);
    this.attachmentName.set(null);
    this.isEditing.set(true);
  }

  protected cancelEditing(): void {
    const currentVendor = this.vendor();
    if (currentVendor) {
      this.hydrateFormFromVendor(currentVendor);
    }

    this.attachmentFile.set(null);
    this.attachmentName.set(null);
    this.validationMessage.set('');
    this.isEditing.set(false);
  }

  protected onFieldChange<K extends keyof VendorFormState>(key: K, value: VendorFormState[K]): void {
    this.form.set({ ...this.form(), [key]: value });
  }

  protected onAttachmentSelected(files: FileList | null): void {
    const selected = files?.item(0) ?? null;
    this.attachmentFile.set(selected);
    this.attachmentName.set(selected?.name ?? null);
  }

  protected clearAttachment(): void {
    this.attachmentFile.set(null);
    this.attachmentName.set(null);
  }

  protected saveVendor(): void {
    const currentVendor = this.vendor();
    if (!currentVendor) {
      return;
    }

    this.validationMessage.set('');
    const payload = this.buildPayload();
    if (!payload) {
      return;
    }

    this.isSaving.set(true);
    this.invoicesService.updateVendor(currentVendor.id, payload).subscribe({
      next: ({ result: updated, documentError }) => {
        if (!updated) {
          this.isSaving.set(false);
          this.validationMessage.set('Unable to update vendor.');
          return;
        }

        this.invoicesService.getVendorById(currentVendor.id).subscribe({
          next: (refreshed) => {
            this.isSaving.set(false);
            this.vendor.set(refreshed ?? null);
            this.attachmentFile.set(null);
            this.attachmentName.set(null);
            this.isEditing.set(false);
            if (documentError) {
              window.KTToast?.show({
                message: `Vendor updated, but the document could not be uploaded. ${documentError}`,
                variant: 'warning',
              });
              return;
            }

            window.KTToast?.show({ message: 'Vendor updated successfully.', variant: 'success' });
          },
          error: () => {
            this.isSaving.set(false);
            this.validationMessage.set('Unable to refresh vendor details after update.');
          },
        });
      },
      error: () => {
        this.isSaving.set(false);
        this.validationMessage.set('Unable to update vendor.');
      },
    });
  }

  protected deleteVendor(): void {
    const currentVendor = this.vendor();
    if (!currentVendor) {
      return;
    }

    const confirmed = window.confirm(`Delete vendor "${currentVendor.name}"?`);
    if (!confirmed) {
      return;
    }

    this.invoicesService.deleteVendor(currentVendor.id).subscribe({
      next: (removed) => {
        if (!removed) {
          window.KTToast?.show({ message: 'Unable to delete vendor.', variant: 'destructive' });
          return;
        }

        window.KTToast?.show({ message: 'Vendor deleted successfully.', variant: 'success' });
        void this.router.navigate(['/catalogs/vendor']);
      },
      error: () => {
        window.KTToast?.show({ message: 'Unable to delete vendor.', variant: 'destructive' });
      },
    });
  }

  private hydrateFormFromVendor(vendor: Vendor): void {
    this.form.set({
      name: vendor.name,
      vendorTypeId: vendor.vendorTypeId,
      description: vendor.description,
      defaultCurrency: vendor.defaultCurrency,
      addressLine1: vendor.addressLine1,
      addressLine2: vendor.addressLine2,
      city: vendor.city,
      stateProvince: vendor.stateProvince,
      postalCode: vendor.postalCode,
      countryCode: vendor.countryCode,
      phoneNumber: vendor.phoneNumber,
      emittedInvoiceConceptIds: [...vendor.emittedInvoiceConceptIds],
    });
  }

  private buildPayload(): UpsertVendorInput | null {
    const currentForm = this.form();
    if (!currentForm.name.trim() || !currentForm.vendorTypeId || !currentForm.defaultCurrency.trim()) {
      this.validationMessage.set('Vendor name, vendor type, and default currency are required.');
      return null;
    }

    if (currentForm.emittedInvoiceConceptIds.length === 0) {
      this.validationMessage.set('Select at least one billing concept.');
      return null;
    }

    return {
      name: currentForm.name.trim(),
      vendorTypeId: currentForm.vendorTypeId,
      description: currentForm.description.trim(),
      defaultCurrency: currentForm.defaultCurrency.trim().toUpperCase(),
      addressLine1: currentForm.addressLine1.trim(),
      addressLine2: currentForm.addressLine2.trim(),
      city: currentForm.city.trim(),
      stateProvince: currentForm.stateProvince.trim(),
      postalCode: currentForm.postalCode.trim(),
      countryCode: currentForm.countryCode.trim().toUpperCase(),
      phoneNumber: currentForm.phoneNumber.trim(),
      emittedInvoiceConceptIds: [...currentForm.emittedInvoiceConceptIds],
      newDocument: this.attachmentFile() ? {
        file: this.attachmentFile() as File,
        fileName: this.attachmentName() || (this.attachmentFile() as File).name,
        description: 'Vendor document',
        type: 'SUPPORT',
      } : null,
    };
  }
}
